import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Scan,
  Bell,
  Bot,
  Activity,
  HeartPulse,
  Undo2,
  Zap,
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

interface SecurityAlert {
  id: string;
  alert_type: string;
  severity: "low" | "medium" | "high" | "critical";
  status: "new" | "investigating" | "resolved" | "false_positive";
  title: string;
  description: string;
  details: Record<string, unknown> | null;
  user_id: string | null;
  ip_address: string | null;
  device_fingerprint: string | null;
  ai_analysis: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  created_at: string;
}

interface AutoAction {
  id: string;
  alert_id: string | null;
  action_type: string;
  action_description: string;
  target_user_id: string | null;
  target_ip: string | null;
  target_device: string | null;
  ai_reasoning: string | null;
  success: boolean;
  error_message: string | null;
  reverted: boolean;
  created_at: string;
}

interface HealthCheck {
  id: string;
  check_type: string;
  status: string;
  details: { checks?: Array<{ type: string; status: string; issues: number; fixed: number; details: string }> } | null;
  issues_found: number;
  auto_fixed: number;
  ai_summary: string | null;
  created_at: string;
}

const severityColors = {
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  critical: "bg-red-500/20 text-red-400 border-red-500/30",
};

const statusColors = {
  new: "bg-red-500/20 text-red-400",
  investigating: "bg-yellow-500/20 text-yellow-400",
  resolved: "bg-green-500/20 text-green-400",
  false_positive: "bg-gray-500/20 text-gray-400",
};

const actionTypeLabels: Record<string, string> = {
  block_ip: "حظر IP",
  block_device: "حظر جهاز",
  force_mfa: "فرض MFA",
  rate_limit_tighten: "تشديد الحدود",
  escalate_alert: "رفع التنبيه",
};

const healthStatusColors: Record<string, string> = {
  ok: "text-green-400",
  warning: "text-yellow-400",
  critical: "text-red-400",
};

const AdminSecurityMonitor = () => {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [autoActions, setAutoActions] = useState<AutoAction[]>([]);
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [healthChecking, setHealthChecking] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<SecurityAlert | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();

  const callSecurityMonitor = async (body: Record<string, unknown>) => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) throw new Error("Not authenticated");
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/security-monitor`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify(body),
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Request failed");
    return data;
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [alertsData, actionsData, healthData] = await Promise.all([
        callSecurityMonitor({ action: "get_alerts" }),
        callSecurityMonitor({ action: "get_auto_actions" }),
        callSecurityMonitor({ action: "get_health_checks" }),
      ]);
      setAlerts(alertsData.alerts || []);
      setAutoActions(actionsData.actions || []);
      setHealthChecks(healthData.checks || []);
    } catch (error) {
      console.error("Error fetching:", error);
      toast({ title: "خطأ", description: "فشل في تحميل البيانات", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const runSecurityScan = async () => {
    try {
      setScanning(true);
      const data = await callSecurityMonitor({ action: "scan" });
      toast({ title: "تم الفحص", description: `تم العثور على ${data.results?.length || 0} تهديدات` });
      await fetchAll();
    } catch (error) {
      toast({ title: "خطأ", description: "فشل الفحص الأمني", variant: "destructive" });
    } finally {
      setScanning(false);
    }
  };

  const runHealthCheck = async () => {
    try {
      setHealthChecking(true);
      const data = await callSecurityMonitor({ action: "health_check" });
      toast({
        title: "فحص صحة النظام",
        description: `الحالة: ${data.results?.overallStatus === "ok" ? "سليم ✅" : data.results?.overallStatus === "warning" ? "تحذيرات ⚠️" : "حرج 🔴"} - ${data.results?.totalIssues || 0} مشكلة`,
      });
      await fetchAll();
    } catch (error) {
      toast({ title: "خطأ", description: "فشل فحص الصحة", variant: "destructive" });
    } finally {
      setHealthChecking(false);
    }
  };

  const updateAlertStatus = async (alertId: string, status: string) => {
    try {
      setUpdating(true);
      await callSecurityMonitor({ action: "update_alert", alertId, status, notes: resolutionNotes });
      toast({ title: "تم التحديث", description: "تم تحديث حالة التنبيه" });
      setSelectedAlert(null);
      setResolutionNotes("");
      await fetchAll();
    } catch (error) {
      toast({ title: "خطأ", description: "فشل التحديث", variant: "destructive" });
    } finally {
      setUpdating(false);
    }
  };

  const revertAction = async (actionId: string) => {
    try {
      await callSecurityMonitor({ action: "revert_action", actionId });
      toast({ title: "تم التراجع", description: "تم التراجع عن الإجراء التلقائي" });
      await fetchAll();
    } catch (error) {
      toast({ title: "خطأ", description: "فشل التراجع", variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchAll();
    const channel = supabase
      .channel("security-monitor-all")
      .on("postgres_changes", { event: "*", schema: "public", table: "security_alerts" }, () => fetchAll())
      .on("postgres_changes", { event: "*", schema: "public", table: "security_auto_actions" }, () => fetchAll())
      .on("postgres_changes", { event: "*", schema: "public", table: "system_health_checks" }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const newAlerts = alerts.filter((a) => a.status === "new");
  const investigatingAlerts = alerts.filter((a) => a.status === "investigating");
  const resolvedAlerts = alerts.filter((a) => a.status === "resolved" || a.status === "false_positive");
  const criticalCount = alerts.filter((a) => a.severity === "critical" && a.status === "new").length;
  const recentActions = autoActions.filter((a) => !a.reverted).slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={ShieldAlert} color="red" value={newAlerts.length} label="تنبيهات جديدة" />
        <StatCard icon={Clock} color="yellow" value={investigatingAlerts.length} label="قيد التحقيق" />
        <StatCard icon={ShieldCheck} color="green" value={resolvedAlerts.length} label="تم حلها" />
        <StatCard icon={Bell} color="red" value={criticalCount} label="حرجة" />
        <StatCard icon={Bot} color="purple" value={autoActions.filter(a => !a.reverted).length} label="إجراءات تلقائية" />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={fetchAll} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> تحديث
        </Button>
        <Button onClick={runSecurityScan} disabled={scanning} className="gap-2">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
          فحص أمني شامل
        </Button>
        <Button onClick={runHealthCheck} disabled={healthChecking} variant="secondary" className="gap-2">
          {healthChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <HeartPulse className="w-4 h-4" />}
          فحص صحة النظام
        </Button>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="alerts" dir="rtl">
        <TabsList className="mb-4">
          <TabsTrigger value="alerts" className="gap-2">
            <Shield className="w-4 h-4" /> التنبيهات
            {newAlerts.length > 0 && <Badge variant="destructive">{newAlerts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="auto_actions" className="gap-2">
            <Bot className="w-4 h-4" /> الإجراءات التلقائية
          </TabsTrigger>
          <TabsTrigger value="health" className="gap-2">
            <HeartPulse className="w-4 h-4" /> صحة النظام
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="pt-6">
              <Tabs defaultValue="new" dir="rtl">
                <TabsList className="mb-4">
                  <TabsTrigger value="new">جديدة {newAlerts.length > 0 && <Badge variant="destructive" className="mr-1">{newAlerts.length}</Badge>}</TabsTrigger>
                  <TabsTrigger value="investigating">قيد التحقيق</TabsTrigger>
                  <TabsTrigger value="resolved">تم حلها</TabsTrigger>
                </TabsList>
                <TabsContent value="new"><AlertsList alerts={newAlerts} onViewAlert={setSelectedAlert} /></TabsContent>
                <TabsContent value="investigating"><AlertsList alerts={investigatingAlerts} onViewAlert={setSelectedAlert} /></TabsContent>
                <TabsContent value="resolved"><AlertsList alerts={resolvedAlerts} onViewAlert={setSelectedAlert} /></TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto Actions Tab */}
        <TabsContent value="auto_actions">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" /> الإجراءات التلقائية للذكاء الاصطناعي
              </CardTitle>
            </CardHeader>
            <CardContent>
              {autoActions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لم يتخذ الذكاء الاصطناعي أي إجراءات بعد</p>
                  <p className="text-sm mt-1">سيتم عرض الإجراءات التلقائية عند اكتشاف تهديدات</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {autoActions.map((action) => (
                      <div
                        key={action.id}
                        className={`p-4 rounded-lg border ${action.reverted ? "opacity-50 border-muted" : action.success ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Zap className={`w-4 h-4 ${action.success ? "text-green-400" : "text-red-400"}`} />
                              <span className="font-medium">
                                {actionTypeLabels[action.action_type] || action.action_type}
                              </span>
                              {action.reverted && <Badge variant="outline" className="text-xs">تم التراجع</Badge>}
                              {!action.success && <Badge variant="destructive" className="text-xs">فشل</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground">{action.action_description}</p>
                            {action.ai_reasoning && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{action.ai_reasoning}</p>
                            )}
                            {action.target_ip && <p className="text-xs font-mono mt-1">IP: {action.target_ip}</p>}
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(action.created_at), "PPpp", { locale: ar })}
                            </p>
                          </div>
                          {!action.reverted && action.success && (
                            <Button variant="ghost" size="sm" onClick={() => revertAction(action.id)} className="gap-1">
                              <Undo2 className="w-3 h-3" /> تراجع
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HeartPulse className="w-5 h-5" /> فحوصات صحة النظام
              </CardTitle>
            </CardHeader>
            <CardContent>
              {healthChecks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>لم يتم إجراء أي فحص بعد</p>
                  <p className="text-sm mt-1">اضغط "فحص صحة النظام" للبدء</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {healthChecks.map((check) => (
                    <div key={check.id} className="p-4 rounded-lg border border-border/50 bg-card/30">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Activity className={`w-5 h-5 ${healthStatusColors[check.status] || "text-muted-foreground"}`} />
                          <span className="font-medium">
                            {check.status === "ok" ? "النظام سليم ✅" : check.status === "warning" ? "تحذيرات ⚠️" : "حالة حرجة 🔴"}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(check.created_at), "PPpp", { locale: ar })}
                        </span>
                      </div>

                      {check.details?.checks && (
                        <div className="grid gap-2 mb-3">
                          {check.details.checks.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-background/50">
                              <span className="flex items-center gap-2">
                                {c.status === "ok" ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                                  c.status === "warning" ? <AlertTriangle className="w-4 h-4 text-yellow-400" /> :
                                    <XCircle className="w-4 h-4 text-red-400" />}
                                {c.details}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {c.issues > 0 ? `${c.issues} مشكلة` : "سليم"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {check.ai_summary && (
                        <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                          <div className="flex items-center gap-2 mb-1">
                            <Bot className="w-4 h-4" />
                            <span className="text-sm font-medium">تحليل الذكاء الاصطناعي</span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{check.ai_summary}</p>
                        </div>
                      )}

                      <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                        <span>مشاكل: {check.issues_found}</span>
                        <span>تم إصلاحها تلقائياً: {check.auto_fixed}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Alert Details Dialog */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> تفاصيل التنبيه
            </DialogTitle>
          </DialogHeader>
          {selectedAlert && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={severityColors[selectedAlert.severity]}>
                  {selectedAlert.severity === "critical" ? "حرج" : selectedAlert.severity === "high" ? "عالي" : selectedAlert.severity === "medium" ? "متوسط" : "منخفض"}
                </Badge>
                <Badge className={statusColors[selectedAlert.status]}>
                  {selectedAlert.status === "new" ? "جديد" : selectedAlert.status === "investigating" ? "قيد التحقيق" : selectedAlert.status === "resolved" ? "تم حله" : "إيجابي خاطئ"}
                </Badge>
              </div>
              <div>
                <h3 className="font-semibold text-lg">{selectedAlert.title}</h3>
                <p className="text-muted-foreground mt-1">{selectedAlert.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">نوع التنبيه:</span> <span className="mr-2">{selectedAlert.alert_type}</span></div>
                <div><span className="text-muted-foreground">التاريخ:</span> <span className="mr-2">{format(new Date(selectedAlert.created_at), "PPpp", { locale: ar })}</span></div>
                {selectedAlert.ip_address && <div><span className="text-muted-foreground">IP:</span> <span className="mr-2 font-mono">{selectedAlert.ip_address}</span></div>}
                {selectedAlert.user_id && <div><span className="text-muted-foreground">المستخدم:</span> <span className="mr-2 font-mono text-xs">{selectedAlert.user_id.substring(0, 8)}...</span></div>}
              </div>
              {selectedAlert.ai_analysis && (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Shield className="w-4 h-4" /> تحليل الذكاء الاصطناعي</h4>
                  <p className="text-sm whitespace-pre-wrap">{selectedAlert.ai_analysis}</p>
                </div>
              )}
              {/* Show related auto-actions */}
              {autoActions.filter(a => a.alert_id === selectedAlert.id).length > 0 && (
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2"><Bot className="w-4 h-4" /> الإجراءات التلقائية المتخذة</h4>
                  {autoActions.filter(a => a.alert_id === selectedAlert.id).map(a => (
                    <div key={a.id} className="flex items-center gap-2 text-sm py-1">
                      <Zap className="w-3 h-3 text-green-400" />
                      <span>{actionTypeLabels[a.action_type] || a.action_type}: {a.action_description}</span>
                    </div>
                  ))}
                </div>
              )}
              {selectedAlert.status !== "resolved" && selectedAlert.status !== "false_positive" && (
                <Textarea placeholder="ملاحظات الحل..." value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} />
              )}
              <DialogFooter className="gap-2">
                {selectedAlert.status === "new" && (
                  <>
                    <Button variant="outline" onClick={() => updateAlertStatus(selectedAlert.id, "investigating")} disabled={updating}>بدء التحقيق</Button>
                    <Button variant="outline" onClick={() => updateAlertStatus(selectedAlert.id, "false_positive")} disabled={updating}>إيجابي خاطئ</Button>
                  </>
                )}
                {selectedAlert.status === "investigating" && (
                  <Button onClick={() => updateAlertStatus(selectedAlert.id, "resolved")} disabled={updating} className="gap-2">
                    {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} تم الحل
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, value, label }: { icon: any; color: string; value: number; label: string }) => (
  <Card className="bg-card/50 backdrop-blur border-border/50">
    <CardContent className="p-3 flex items-center gap-3">
      <div className={`p-2 rounded-full bg-${color}-500/20`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div>
        <p className="text-xl font-bold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const AlertsList = ({ alerts, onViewAlert }: { alerts: SecurityAlert[]; onViewAlert: (a: SecurityAlert) => void }) => {
  if (alerts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <ShieldCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>لا توجد تنبيهات</p>
      </div>
    );
  }
  return (
    <ScrollArea className="h-[400px]">
      <div className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="p-4 rounded-lg border border-border/50 bg-card/30 hover:bg-card/50 cursor-pointer transition-colors"
            onClick={() => onViewAlert(alert)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={severityColors[alert.severity]} variant="outline">
                    {alert.severity === "critical" ? "حرج" : alert.severity === "high" ? "عالي" : alert.severity === "medium" ? "متوسط" : "منخفض"}
                  </Badge>
                  <h3 className="font-medium">{alert.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-1">{alert.description}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {format(new Date(alert.created_at), "PPpp", { locale: ar })}
                </p>
              </div>
              <Button variant="ghost" size="sm"><AlertTriangle className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default AdminSecurityMonitor;
